import React from 'react';
import { 
  DollarSign, 
  MessageSquare,
  Send
} from 'lucide-react';
import { Input, Textarea, Select } from '@/components/ui/FormComponents';
import { usePaymentManagement } from '../hooks/usePaymentManagement';
import { PAYMENT_METHODS } from '../constants/paymentConstants';

export const PaymentManagement = ({ project, onUpdate, setToast, onClose }) => {
  const {
    activeTab,
    setActiveTab,
    register,
    handleSubmit,
    setValue,
    paymentMethod,
    onSubmitCredit,
    onSubmitFollowUp,
    isRejected,
    submitting,
    serverError,
    errors
  } = usePaymentManagement(project, onUpdate, setToast, onClose);

  return (
    <div className="w-full flex flex-col gap-0 animate-in fade-in duration-500 p-1">


      {/* Tab Switcher */}
      <div className="flex w-full mb-6">
        <button
          onClick={() => setActiveTab('credit')}
          className={`flex-1 py-4 flex items-center justify-center gap-3 transition-all border-b-2 font-black text-[11px] uppercase tracking-widest ${
            activeTab === 'credit' 
            ? 'border-blue-500 text-blue-600 ' 
            : 'border-slate-100 text-slate-400 hover:text-slate-600 hover:bg-slate-50'
          }`}
        >
          <DollarSign className="w-4 h-4" />
          Credit (Money In)
        </button>
        <button
          onClick={() => setActiveTab('follow-up')}
          className={`flex-1 py-4 flex items-center justify-center gap-3 transition-all border-b-2 font-black text-[11px] uppercase tracking-widest ${
            activeTab === 'follow-up' 
            ? 'border-rose-500 text-rose-600 bg-rose-50/50' 
            : 'border-slate-100 text-slate-400 hover:text-slate-600 hover:bg-slate-50'
          }`}
        >
          <MessageSquare className="w-4 h-4" />
          Payment Follow-Up
        </button>
      </div>

      <div className="bg-white">
        {isRejected && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-md flex items-start gap-3">
            <div className="flex flex-col">
              <span className="text-[13px] font-medium text-rose-900">Submission Rejected</span>
              <p className="text-[12px] font-medium text-rose-600 mt-0.5">Please update and resubmit.</p>
            </div>
          </div>
        )}

        {serverError && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-lg animate-in fade-in slide-in-from-top-2">
             <p className="text-[11px] font-bold text-rose-700 leading-tight">
               {serverError}
             </p>
          </div>
        )}

        {activeTab === 'credit' ? (
          <form onSubmit={handleSubmit(onSubmitCredit)} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[13px] font-medium text-slate-500">Transaction Date</label>
                <Input type="date" {...register('transactionDate')} className="text-[12px] font-medium text-slate-500 rounded-md border-slate-200" />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[13px] font-medium text-slate-500">Amount (₹)</label>
                <Input type="number" placeholder="0.00" {...register('amount')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.amount ? 'border-rose-500' : 'border-slate-200'}`} />
                {errors.amount && <span className="text-[10px] text-rose-500 font-medium">{errors.amount.message}</span>}
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[13px] font-medium text-slate-500">Payment Type</label>
                <Select 
                  {...register('paymentType')} 
                  className={`${errors.paymentType ? 'border-rose-500' : 'border-slate-200'}`}
                >
                  <option value="">Auto-detect</option>
                  <option value="Advance Payment">Advance Payment</option>
                  <option value="Milestone Payment">Milestone Payment</option>
                  <option value="Final Payment">Final Payment</option>
                </Select>
                {errors.paymentType && <span className="text-[10px] text-rose-500 font-medium">{errors.paymentType.message}</span>}
              </div>
            </div>
 
            <div className="flex flex-col gap-2">
              <label className="text-[13px] font-medium text-slate-500">Payment Method</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {PAYMENT_METHODS.map((method) => {
                  const isActive = paymentMethod === method.id;
                  return (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setValue('paymentMethod', method.id)}
                      className={`py-2.5 rounded-md border text-[12px] font-medium transition-all ${
                        isActive 
                        ? 'border-[#f97316] bg-blue-50 text-[#f97316]' 
                        : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      {method.label}
                    </button>
                  );
                })}
              </div>
            </div>
 
            {/* Conditional Fields based on Payment Method */}
            {paymentMethod === 'bank_transfer' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-medium text-slate-500">Transaction / ref ID</label>
                  <Input placeholder="TRX123..." {...register('refId')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.refId ? 'border-rose-500' : 'border-slate-200'}`} />
                  {errors.refId && <span className="text-[10px] text-rose-500 font-medium">{errors.refId.message}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-medium text-slate-500">Bank Name</label>
                  <Input placeholder="e.g. HDFC" {...register('bankName')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.bankName ? 'border-rose-500' : 'border-slate-200'}`} />
                  {errors.bankName && <span className="text-[10px] text-rose-500 font-medium">{errors.bankName.message}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-medium text-slate-500">Branch</label>
                  <Input placeholder="Branch Name" {...register('branch')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.branch ? 'border-rose-500' : 'border-slate-200'}`} />
                  {errors.branch && <span className="text-[10px] text-rose-500 font-medium">{errors.branch.message}</span>}
                </div>
              </div>
            )}
 
            {paymentMethod === 'upi_qr' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-medium text-slate-500">UPI UTR / Ref No.</label>
                  <Input placeholder="TRX123..." {...register('refId')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.refId ? 'border-rose-500' : 'border-slate-200'}`} />
                  {errors.refId && <span className="text-[10px] text-rose-500 font-medium">{errors.refId.message}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-medium text-slate-500">UPI Phone Number</label>
                  <Input placeholder="10-digit Phone" maxLength={10} {...register('upiPhone')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.upiPhone ? 'border-rose-500' : 'border-slate-200'}`} />
                  {errors.upiPhone && <span className="text-[10px] text-rose-500 font-medium">{errors.upiPhone.message}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-medium text-slate-500">Linked Bank</label>
                  <Input placeholder="Bank Name" {...register('bankName')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.bankName ? 'border-rose-500' : 'border-slate-200'}`} />
                  {errors.bankName && <span className="text-[10px] text-rose-500 font-medium">{errors.bankName.message}</span>}
                </div>
              </div>
            )}
 
            {paymentMethod === 'cash' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-medium text-slate-500">Receiver Name</label>
                  <Input placeholder="Who received cash" {...register('receiverName')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.receiverName ? 'border-rose-500' : 'border-slate-200'}`} />
                  {errors.receiverName && <span className="text-[10px] text-rose-500 font-medium">{errors.receiverName.message}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-medium text-slate-500">Receiver Phone</label>
                  <Input placeholder="10-digit number" maxLength={10} {...register('receiverPhone')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.receiverPhone ? 'border-rose-500' : 'border-slate-200'}`} />
                  {errors.receiverPhone && <span className="text-[10px] text-rose-500 font-medium">{errors.receiverPhone.message}</span>}
                </div>
              </div>
            )}
 
            {paymentMethod === 'cheque' && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-medium text-slate-500">Reference ID</label>
                    <Input placeholder="TRX123..." {...register('refId')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.refId ? 'border-rose-500' : 'border-slate-200'}`} />
                    {errors.refId && <span className="text-[10px] text-rose-500 font-medium">{errors.refId.message}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-medium text-slate-500">Cheque Number</label>
                    <Input placeholder="6-digit No." maxLength={6} {...register('chequeNumber')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.chequeNumber ? 'border-rose-500' : 'border-slate-200'}`} />
                    {errors.chequeNumber && <span className="text-[10px] text-rose-500 font-medium">{errors.chequeNumber.message}</span>}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-medium text-slate-500">Cheque Date</label>
                    <Input type="date" {...register('chequeDate')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.chequeDate ? 'border-rose-500' : 'border-slate-200'}`} />
                    {errors.chequeDate && <span className="text-[10px] text-rose-500 font-medium">{errors.chequeDate.message}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-medium text-slate-500">Issuing Bank</label>
                    <Input placeholder="Bank name on cheque" {...register('bankName')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.bankName ? 'border-rose-500' : 'border-slate-200'}`} />
                    {errors.bankName && <span className="text-[10px] text-rose-500 font-medium">{errors.bankName.message}</span>}
                  </div>
                </div>
              </>
            )}
 
            <div className="flex flex-col gap-2">
              <label className="text-[13px] font-medium text-slate-500">Recipient</label>
              <Input placeholder="e.g. Verve Kitchens" {...register('recipient')} className={`text-[12px] font-medium text-slate-500 rounded-md ${errors.recipient ? 'border-rose-500' : 'border-slate-200'}`} />
              {errors.recipient && <span className="text-[10px] text-rose-500 font-medium">{errors.recipient.message}</span>}
            </div>
 
            <div className="flex flex-col gap-2">
              <label className="text-[13px] font-medium text-slate-500">Internal Notes</label>
              <Textarea placeholder="Internal reference..." {...register('internalNotes')} className={`text-[12px] font-medium text-slate-500 rounded-md min-h-[80px] ${errors.internalNotes ? 'border-rose-500' : 'border-slate-200'}`} />
              {errors.internalNotes && <span className="text-[10px] text-rose-500 font-medium">{errors.internalNotes.message}</span>}
            </div>


            <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:flex-1 py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-500 rounded-md text-[12px] font-medium transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:flex-1 py-2.5 bg-[#64748b] cursor-pointer hover:bg-[#475569] text-white rounded-md text-[12px] font-medium transition-all disabled:opacity-50 disabled:grayscale flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Recording...
                  </>
                ) : 'Record Payment'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit(onSubmitFollowUp)} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[13px] font-medium text-slate-500">Date</label>
                <Input type="date" {...register('followUpDate')} className="text-[12px] font-medium text-slate-500 rounded-md border-slate-200" />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[13px] font-medium text-slate-500">Approx. Payment Date</label>
                <Input type="date" {...register('approxPaymentDate')} className="text-[12px] font-medium text-slate-500 rounded-md border-slate-200" />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[13px] font-medium text-slate-500">Reason for Non Payment</label>
              <Input placeholder="e.g. Customer out of station" {...register('reason')} className="text-[12px] font-medium text-slate-500 rounded-md border-slate-200" />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[13px] font-medium text-slate-500">Description</label>
              <Textarea placeholder="Additional details..." {...register('description')} className="text-[12px] font-medium text-slate-500 rounded-md border-slate-200 min-h-[100px]" />
            </div>

            <div className="pt-4 border-t border-slate-100">
              <button
                type="submit"
                className="w-full py-2.5 bg-[#da2a2a] hover:bg-[#b91c1c] text-white rounded-md text-[13px] font-medium transition-all flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                Record Payment Follow-up
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

